/**
 * Credit Calculation System
 * 
 * Credits consumed = Base cost × Size multiplier × Persona multiplier × Depth multiplier
 */

import type { AnalysisDepth, ModuleType } from "./types";
import { SUBSCRIPTION_TIERS, TOPUP_CREDIT_PACKS } from "./pricing";

// Base costs by module type
const BASE_COSTS: Record<ModuleType, Record<AnalysisDepth, number>> = {
  // Customer-facing model: one standard AI run = 10 credits.
  // Quick clarity is handled separately in product flows; meaningful generation/evaluation/refinement consumes a standard run.
  evaluation: { quick: 10, deep: 10 },
  generation: { quick: 10, deep: 10 },
  refinement: { quick: 10, deep: 10 },
  prototype: { quick: 30, deep: 50 },
};

// Size multiplier tiers (based on character count)
const SIZE_TIERS = [
  { maxChars: 500, multiplier: 1.0 },
  { maxChars: 2000, multiplier: 1.5 },
  { maxChars: 5000, multiplier: 2.0 },
  { maxChars: 10000, multiplier: 3.0 },
  { maxChars: Infinity, multiplier: 4.0 },
];

// Persona multiplier tiers
const PERSONA_TIERS = [
  { maxPersonas: 1, multiplier: 1.0 },
  { maxPersonas: 3, multiplier: 1.25 },
  { maxPersonas: 5, multiplier: 1.5 },
  { maxPersonas: Infinity, multiplier: 2.0 },
];

// Depth multiplier
const DEPTH_MULTIPLIERS: Record<AnalysisDepth, number> = {
  quick: 1.0,
  deep: 2.0,
};

// Plan credit allocations
export const PLAN_CREDITS = {
  free: SUBSCRIPTION_TIERS.free.monthlyCredits,
  solo: SUBSCRIPTION_TIERS.solo.monthlyCredits,
  startup: SUBSCRIPTION_TIERS.startup.monthlyCredits,
  business: SUBSCRIPTION_TIERS.business.monthlyCredits,
  enterprise: Infinity,
} as const;

// Top-up product definitions. Credits remain the internal accounting unit; 10 credits = 1 standard AI run.
export const TOPUP_PRODUCTS = TOPUP_CREDIT_PACKS.map((pack) => ({
  name: pack.name,
  legacyName: pack.legacyName,
  credits: pack.credits,
  priceCents: pack.priceCents,
  extraAiRuns: pack.extraAiRuns,
  tagline: pack.tagline,
  perCreditCost: pack.priceCents / 100 / pack.credits,
})) as typeof TOPUP_CREDIT_PACKS extends readonly any[] ? Array<{
  name: string;
  legacyName: string;
  credits: number;
  priceCents: number;
  extraAiRuns: number;
  tagline: string;
  perCreditCost: number;
}> : never;

export interface CreditEstimateInput {
  mode: ModuleType;
  depth: AnalysisDepth;
  inputLength: number; // in characters
  personaCount: number;
}

export interface CreditEstimateBreakdown {
  baseCost: number;
  sizeMultiplier: number;
  personaMultiplier: number;
  depthMultiplier: number;
  totalCredits: number;
  details: {
    mode: ModuleType;
    depth: AnalysisDepth;
    inputLength: number;
    personaCount: number;
  };
}

/**
 * Get the size multiplier based on input character count
 */
export function getSizeMultiplier(charCount: number): number {
  for (const tier of SIZE_TIERS) {
    if (charCount <= tier.maxChars) {
      return tier.multiplier;
    }
  }
  return SIZE_TIERS[SIZE_TIERS.length - 1].multiplier;
}

/**
 * Get the persona multiplier based on persona count
 */
export function getPersonaMultiplier(personaCount: number): number {
  for (const tier of PERSONA_TIERS) {
    if (personaCount <= tier.maxPersonas) {
      return tier.multiplier;
    }
  }
  return PERSONA_TIERS[PERSONA_TIERS.length - 1].multiplier;
}

/**
 * Calculate the total credits required for a run
 */
export function calculateCredits(input: CreditEstimateInput): CreditEstimateBreakdown {
  const baseCost = BASE_COSTS[input.mode][input.depth];
  const sizeMultiplier = getSizeMultiplier(input.inputLength);
  const personaMultiplier = getPersonaMultiplier(input.personaCount);
  const depthMultiplier = DEPTH_MULTIPLIERS[input.depth];

  // Keep the customer-facing model simple: standard actions consume one AI run.
  // Size/persona/depth remain visible for future heavier operations, but normal runs are not tiny fractional credits.
  const rawTotal = baseCost;
  const totalCredits = Math.ceil(rawTotal);

  return {
    baseCost,
    sizeMultiplier,
    personaMultiplier,
    depthMultiplier,
    totalCredits,
    details: {
      mode: input.mode,
      depth: input.depth,
      inputLength: input.inputLength,
      personaCount: input.personaCount,
    },
  };
}

/**
 * Format credits for display
 */
export function formatCredits(credits: number): string {
  if (credits === undefined || credits === null || Number.isNaN(credits as number)) {
    return "0";
  }
  if (credits === Infinity) {
    return "Unlimited";
  }
  return credits.toLocaleString();
}

/**
 * Get a human-readable description of the size tier
 */
export function getSizeTierLabel(charCount: number): string {
  if (charCount <= 500) return "Small";
  if (charCount <= 2000) return "Medium";
  if (charCount <= 5000) return "Large";
  if (charCount <= 10000) return "Very Large";
  return "Extra Large";
}

/**
 * Check if a user has sufficient credits
 */
export function hasSufficientCredits(
  required: number,
  monthlyRemaining: number,
  topupCredits: number
): boolean {
  return monthlyRemaining + topupCredits >= required;
}

/**
 * Calculate days until monthly reset
 */
export function getDaysUntilReset(resetDate: string | Date): number {
  const reset = new Date(resetDate);
  const now = new Date();
  const diffMs = reset.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}
